import { IAdvizerVideo } from "@/models/AdvizerVideo";
import { IUserVideoReaction } from "@/types/UserDataTypes";

export interface ILevelCheckParams {
  isLoggedIn: boolean;
  userVideosWatched: IAdvizerVideo[];
  userVideoData: Record<
    string,
    {
      percentWatched?: number;
      secondsWatched?: number;
      totalSecondsWatched?: number;
    }
  >;
  userVideoReactions: Record<string, IUserVideoReaction>;
  inProgressPlaylistKeys: string[];
  completedPlaylistKeys: string[];
  totalSecondsWatched: number;
}

export interface IUserLevelConfig {
  title: string;
  href: string;
  buttonText?: string;
  buttonHref?: string;
  level: number;
  tooltip?: string;
  statusString?: string;
  checkComplete: (params: ILevelCheckParams) => boolean;
  checkStatus?: (params: ILevelCheckParams) => string;
}

export const levelConfigs: IUserLevelConfig[] = [
  {
    title: "Create a free account or sign in",
    href: "/login",
    buttonText: "Sign in",
    buttonHref: "/login",
    checkComplete: (params: ILevelCheckParams) => {
      return params.isLoggedIn;
    },
  },
  {
    title: "Play a video",
    href: "/explore",
    buttonText: "Explore Videos",
    buttonHref: "/explore",
    checkComplete: (params: ILevelCheckParams) => {
      return params.userVideosWatched.length > 0;
    },
  },
  {
    title: "Watch 1 minute of video",
    href: "/explore",
    buttonText: "Explore Videos",
    buttonHref: "/explore",
    checkComplete: (params: ILevelCheckParams) => {
      return (params.totalSecondsWatched || 0) > 60;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const secondsNeeded = 60 - params.totalSecondsWatched || 0;
      return `${secondsNeeded} seconds to go!`;
    },
  },
  {
    title: "React to a video you watched",
    href: "/profile#video-history",
    buttonText: "View Video History",
    buttonHref: "/profile#video-history",
    tooltip:
      "Click the heart, thumb up, or thumb down icons on a video to react",
    checkComplete: (params: ILevelCheckParams) => {
      return Object.keys(params.userVideoReactions || {}).length > 0;
    },
  },
  {
    title: "Start a playlist",
    href: "/?step=select-subjects",
    buttonText: "Get Recommendations",
    buttonHref: "/?step=select-subjects",
    checkComplete: (params: ILevelCheckParams) => {
      return (
        params.inProgressPlaylistKeys?.length > 0 ||
        params.completedPlaylistKeys?.length > 0
      );
    },
  },
  {
    title: "Complete a playlist",
    href: "/profile#playlists-in-progress",
    buttonText: "View active playlists",
    buttonHref: "/profile#playlists-in-progress",
    checkComplete: (params: ILevelCheckParams) => {
      return params.completedPlaylistKeys?.length > 0;
    },
  },
  {
    title: "React to 5 videos",
    href: "/profile#reactions",
    buttonText: "View Reactions",
    buttonHref: "/profile#reactions",
    checkComplete: (params: ILevelCheckParams) => {
      return Object.keys(params.userVideoReactions || {}).length >= 5;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const totalReactions = Object.keys(
        params.userVideoReactions || {},
      ).length;
      const reactionsNeeded = 5 - totalReactions;
      return `${reactionsNeeded} more to go!`;
    },
  },
  {
    title: "Watch 5 minutes of video",
    href: "/explore",
    buttonText: "Explore Videos",
    buttonHref: "/explore",
    checkComplete: (params: ILevelCheckParams) => {
      return (params.totalSecondsWatched || 0) > 5 * 60;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const secondsNeeded = 5 * 60 - (params.totalSecondsWatched || 0);
      const minutesNeeded = Math.ceil(secondsNeeded / 60);
      return `${minutesNeeded} mins to go!`;
    },
  },
  {
    title: "Watch videos from 5 different Advizers",
    href: "/explore",
    buttonText: "Explore Advizers",
    buttonHref: "/explore",
    checkComplete: (params: ILevelCheckParams) => {
      const uniqueAdvizerIds = new Set(
        params.userVideosWatched.map((video) => video.advizerId),
      );
      return uniqueAdvizerIds.size >= 5;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const uniqueAdvizerIds = new Set(
        params.userVideosWatched.map((video) => video.advizerId),
      );
      const needed = 5 - uniqueAdvizerIds.size;
      return `${needed} more to go!`;
    },
  },
  {
    title: "Complete 3 playlists",
    href: "/profile#playlists-in-progress",
    buttonText: "View active playlists",
    buttonHref: "/profile#playlists-in-progress",
    checkComplete: (params: ILevelCheckParams) => {
      return (params.completedPlaylistKeys?.length || 0) >= 3;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const completed = params.completedPlaylistKeys?.length || 0;
      const needed = 3 - completed;
      return `${needed} more to go!`;
    },
  },
  {
    title: "React to 10 videos",
    href: "/profile#reactions",
    buttonText: "View Reactions",
    buttonHref: "/profile#reactions",
    checkComplete: (params: ILevelCheckParams) => {
      return Object.keys(params.userVideoReactions || {}).length >= 10;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const totalReactions = Object.keys(
        params.userVideoReactions || {},
      ).length;
      const reactionsNeeded = 10 - totalReactions;
      return `${reactionsNeeded} more to go!`;
    },
  },
  {
    title: "Watch 15 minutes of video",
    href: "/explore",
    buttonText: "Explore Videos",
    buttonHref: "/explore",
    checkComplete: (params: ILevelCheckParams) => {
      return (params.totalSecondsWatched || 0) > 15 * 60;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const secondsNeeded = 15 * 60 - (params.totalSecondsWatched || 0);
      const minutesNeeded = Math.ceil(secondsNeeded / 60);
      return `${minutesNeeded} min to go!`;
    },
  },
  {
    title: "Watch videos from 10 different Advizers",
    href: "/explore",
    buttonText: "Explore Advizers",
    buttonHref: "/explore",
    checkComplete: (params: ILevelCheckParams) => {
      const uniqueAdvizerIds = new Set(
        params.userVideosWatched.map((video) => video.advizerId),
      );
      return uniqueAdvizerIds.size >= 10;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const uniqueAdvizerIds = new Set(
        params.userVideosWatched.map((video) => video.advizerId),
      );
      const needed = 10 - uniqueAdvizerIds.size;
      return `${needed} more to go!`;
    },
  },
  {
    title: "Complete 5 playlists",
    href: "/profile#playlists-in-progress",
    buttonText: "View active playlists",
    buttonHref: "/profile#playlists-in-progress",
    checkComplete: (params: ILevelCheckParams) => {
      return (params.completedPlaylistKeys?.length || 0) >= 5;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const completed = params.completedPlaylistKeys?.length || 0;
      const needed = 5 - completed;
      return `${needed} more to go!`;
    },
  },
  {
    title: "React to 25 videos",
    href: "/profile#reactions",
    buttonText: "View Reactions",
    buttonHref: "/profile#reactions",
    checkComplete: (params: ILevelCheckParams) => {
      return Object.keys(params.userVideoReactions || {}).length >= 25;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const totalReactions = Object.keys(
        params.userVideoReactions || {},
      ).length;
      const reactionsNeeded = 25 - totalReactions;
      return `${reactionsNeeded} more to go!`;
    },
  },
  {
    title: "Watch 30 minutes of video",
    href: "/explore",
    buttonText: "Explore Videos",
    buttonHref: "/explore",
    checkComplete: (params: ILevelCheckParams) => {
      return (params.totalSecondsWatched || 0) > 30 * 60;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const secondsNeeded = 30 * 60 - (params.totalSecondsWatched || 0);
      const minutesNeeded = Math.ceil(secondsNeeded / 60);
      return `${minutesNeeded} min to go!`;
    },
  },
  {
    title: "Complete 10 playlists",
    href: "/profile#playlists-in-progress",
    buttonText: "View active playlists",
    buttonHref: "/profile#playlists-in-progress",
    checkComplete: (params: ILevelCheckParams) => {
      return (params.completedPlaylistKeys?.length || 0) >= 10;
    },
    checkStatus: (params: ILevelCheckParams) => {
      const completed = params.completedPlaylistKeys?.length || 0;
      const needed = 10 - completed;
      return `${needed} more to go!`;
    },
  },
].map((levelConfig, index) => {
  return {
    ...levelConfig,
    level: index + 1,
  };
});
